import React, { createContext, useMemo, useState, useEffect, useRef, useCallback, forwardRef, useReducer, Suspense, createElement, useContext } from 'react';
import { UndoIcon, MenuIcon, ControlsIcon, SelectIcon, SunIcon, MoonIcon } from '@sanity/icons';
import { useToast, ToastProvider, BoundaryElementProvider, PortalProvider, Container, Stack, Text, Button, Card, Code, ErrorBoundary, Flex, Box, Checkbox, TextInput, Select, TextArea, Breadcrumbs, LayerProvider, MenuButton, Menu, MenuItem, Switch, Spinner, Layer, TabList, Tab, TabPanel, Tree, TreeItem } from '@sanity/ui';
import axe from 'axe-core';
import pako from 'pako';
import styled from 'styled-components';
import { set } from 'segmented-property';

const ScopeContext = /*#__PURE__*/createContext(null);

function ScopeProvider(_props) {
  const {
    axeResults,
    children,
    registerProp,
    schemas,
    scope,
    setPropValue,
    story,
    title,
    unregisterProp,
    value
  } = _props;
  const contextValue = useMemo(() => ({
    axeResults,
    schemas,
    scope,
    story,
    registerProp,
    setPropValue,
    title,
    unregisterProp,
    value
  }), [axeResults, schemas, scope, story, registerProp, setPropValue, title, unregisterProp, value]);
  return /*#__PURE__*/React.createElement(ScopeContext.Provider, {
    value: contextValue
  }, children);
}

function defineScope(name, title, stories) {
  return {
    name,
    title,
    stories
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const IGNORE_VIOLATION_IDS = ['landmark-one-main', 'page-has-heading-one'];
function useAxeResults(props) {
  const {
    enabled = true,
    key
  } = props;
  const [results, setResults] = useState(null);
  const {
    push: pushToast
  } = useToast();
  useEffect(() => {
    if (!enabled) return;

    try {
      setResults(null);
      axe.run().then(results => {
        // Remove ignored violations
        for (let i = 0; results.violations.length; i += 1) {
          const v = results.violations[i];

          if (v && IGNORE_VIOLATION_IDS.includes(v.id)) {
            results.violations.splice(i, 1);
            i -= 1;
          }
        }

        setResults(results);
      }).catch(err => {
        // eslint-disable-next-line no-console
        console.error(err);
        pushToast({
          title: 'Axe: failed to run',
          description: err.message,
          status: 'error'
        });
      });
    } catch (axeRunError) {
      // eslint-disable-next-line no-console
      console.error(axeRunError);

      if (axeRunError instanceof Error) {
        pushToast({
          title: 'Axe: unknown error',
          description: axeRunError.message,
          status: 'error'
        });
      } else {
        pushToast({
          title: 'Axe: unknown error',
          description: String(axeRunError),
          status: 'error'
        });
      }
    }
  }, [enabled, key, pushToast]);
  return results;
}

const features = {
  axe: false
};

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

const qs = {
  parse(str) {
    const params = new URLSearchParams('?' + str);
    const q = {};
    params.forEach((value, key) => {
      q[key] = value;
    });
    return q;
  },

  stringify(q) {
    return Object.entries(q).map(([key, value]) => `${key}=${value}`).join('&');
  }

};

function resolveLocation(scopes, path) {
  let segments = path.split('/').slice(1).filter(Boolean);
  const p = segments.join('/');

  if (segments.length === 0) {
    return {
      scope: null,
      story: null
    };
  }

  const scope = scopes.find(scope => {
    return p.startsWith(`${scope.name}/`);
  }) || null;
  const len = (scope == null ? void 0 : scope.name.split('/').length) || 0;
  segments = segments.slice(len);
  const story = scope && scope.stories.find(story => story.name === segments.join('/')) || null;
  return {
    scope,
    story
  };
}

function workshopReducer(state, msg) {
  if (msg.type === 'workshop/ready') {
    return state;
  }

  if (msg.type === 'workshop/setValue') {
    return _extends({}, state, {
      value: msg.value
    });
  }

  if (msg.type === 'workshop/registerProp') {
    return _extends({}, state, {
      schemas: state.schemas.concat([msg.schema])
    });
  }

  if (msg.type === 'workshop/unregisterProp') {
    return _extends({}, state, {
      schemas: state.schemas.filter(s => s.name !== msg.name)
    });
  }

  if (msg.type === 'workshop/setPropValue') {
    return _extends({}, state, {
      value: _extends({}, state.value, {
        [msg.name]: msg.value
      })
    });
  }

  if (msg.type === 'workshop/queue') {
    return msg.queue.reduce(workshopReducer, state);
  } // console.warn('unhandled msg', msg)


  return state;
}

const WorkshopContext = /*#__PURE__*/createContext(null);

function useParent() {
  const messageQueueRef = useRef([]);
  const flushTimeoutRef = useRef(null);

  const _flushMessageQueue = useCallback(() => {
    if (flushTimeoutRef.current) return;
    flushTimeoutRef.current = setTimeout(() => {
      const queue = messageQueueRef.current;
      messageQueueRef.current = [];
      flushTimeoutRef.current = null;

      if (queue.length === 1) {
        var _parent;

        (_parent = parent) == null ? void 0 : _parent.postMessage(queue[0], window.location.origin);
      } else {
        var _parent2;

        (_parent2 = parent) == null ? void 0 : _parent2.postMessage({
          type: 'workshop/queue',
          queue
        }, window.location.origin);
      }
    }, 0);
  }, []);

  const postMessage = useCallback(msg => {
    messageQueueRef.current.push(msg);

    _flushMessageQueue();
  }, [_flushMessageQueue]);
  return useMemo(() => ({
    postMessage
  }), [postMessage]);
}

function WorkshopFrame(props) {
  const [boundaryElement, setBoundaryElement] = useState(null);
  const [portalElement, setPortalElement] = useState(null);
  return /*#__PURE__*/React.createElement(ToastProvider, null, /*#__PURE__*/React.createElement(BoundaryElementProvider, {
    element: boundaryElement
  }, /*#__PURE__*/React.createElement(PortalProvider, {
    element: portalElement
  }, /*#__PURE__*/React.createElement(InnerWorkshopFrame, _extends({}, props, {
    ref: setBoundaryElement
  })), /*#__PURE__*/React.createElement("div", {
    "data-portal": "",
    ref: setPortalElement
  }))));
}
const InnerWorkshopFrame = /*#__PURE__*/forwardRef(function InnerWorkshopFrame(props, ref) {
  const {
    frameUrl,
    scopes,
    setScheme,
    title
  } = props;
  const query = useMemo(() => {
    if (typeof window === 'undefined') return {};
    return qs.parse(window.location.search.substr(1));
  }, []);
  const value = useMemo(() => query.value ? JSON.parse(decodeURIComponent(query.value)) : {}, [query]);
  const [path, setPath] = useState(query.path || '/');
  const [state, dispatch] = useReducer(workshopReducer, {
    axeResults: null,
    schemas: [],
    value
  });
  const {
    postMessage
  } = useParent();
  const {
    scope,
    story
  } = useMemo(() => resolveLocation(scopes, path), [path, scopes]);
  const loc = useMemo(() => ({
    path
  }), [path]);
  const axeResults = useAxeResults({
    enabled: features.axe ,
    key: (story == null ? void 0 : story.name) || null
  });
  const [mounted, setMounted] = useState(false);
  const pushLocation = useCallback(newLoc => {
    const msg = {
      type: 'workshop/frame/pushLocation',
      location: newLoc
    };
    postMessage(msg);
  }, [postMessage]);
  const replaceLocation = useCallback(newLoc => {
    const msg = {
      type: 'workshop/frame/replaceLocation',
      location: newLoc
    };
    postMessage(msg);
  }, [postMessage]);
  const registerProp = useCallback(schema => {
    const msg = {
      type: 'workshop/registerProp',
      schema
    };
    postMessage(msg);
    dispatch(msg);
  }, [postMessage]);
  const unregisterProp = useCallback(name => {
    const msg = {
      type: 'workshop/unregisterProp',
      name
    };
    postMessage(msg);
    dispatch(msg);
  }, [postMessage]);
  const setPropValue = useCallback((name, value) => {
    const msg = {
      type: 'workshop/setPropValue',
      name,
      value
    };
    postMessage(msg);
    dispatch(msg);
  }, [postMessage]);
  const contextValue = useMemo(() => {
    return {
      frameUrl,
      location: loc,
      pushLocation,
      replaceLocation,
      scope,
      scopes,
      story,
      title
    };
  }, [frameUrl, loc, pushLocation, replaceLocation, scope, scopes, story, title]);
  useEffect(() => {
    return;
  }, [axeResults, postMessage]); // Set initial scheme

  useEffect(() => setScheme(query.scheme), [query.scheme, setScheme]);
  useEffect(() => {
    const readyMsg = {
      type: 'workshop/ready',
      path
    };
    postMessage(readyMsg);

    const handleMainMsg = msg => {
      if (msg.type === 'workshop/main/setLocation') {
        setPath(msg.path);
        setScheme(msg.scheme);
        return;
      }

      dispatch(msg);
    };

    const handleMessage = event => {
      const msg = event.data;

      if (isRecord(msg) && typeof msg.type === 'string' && msg.type.startsWith('workshop/')) {
        handleMainMsg(msg);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [path, postMessage, setScheme]); // Set mounted

  useEffect(() => setMounted(true), []);
  const [errorParams, setErrorParams] = useState(null);
  const {
    error,
    info: errorInfo
  } = errorParams || {};
  const handleReset = useCallback(() => setErrorParams(null), []);

  if (error) {
    return /*#__PURE__*/React.createElement(Container, {
      paddingX: 4,
      paddingY: [5, 5, 6],
      sizing: "border"
    }, /*#__PURE__*/React.createElement(Stack, {
      height: "fill",
      space: 5
    }, /*#__PURE__*/React.createElement(Text, {
      as: "h1",
      weight: "bold"
    }, "Workshop error"), /*#__PURE__*/React.createElement(Stack, null, /*#__PURE__*/React.createElement(Button, {
      icon: UndoIcon,
      onClick: handleReset,
      text: "Retry"
    })), /*#__PURE__*/React.createElement(Stack, {
      space: 3
    }, /*#__PURE__*/React.createElement(Text, {
      as: "h2",
      size: 1,
      weight: "semibold"
    }, "Error stack"), /*#__PURE__*/React.createElement(Card, {
      border: true,
      overflow: "auto",
      padding: 3,
      radius: 2,
      tone: "critical"
    }, /*#__PURE__*/React.createElement(Code, null, error.stack))), (errorInfo == null ? void 0 : errorInfo.componentStack) && /*#__PURE__*/React.createElement(Stack, {
      space: 3
    }, /*#__PURE__*/React.createElement(Text, {
      as: "h2",
      size: 1,
      weight: "semibold"
    }, "Component stack"), /*#__PURE__*/React.createElement(Card, {
      border: true,
      overflow: "auto",
      padding: 3,
      radius: 2,
      tone: "critical"
    }, /*#__PURE__*/React.createElement(Code, null, '    ' + errorInfo.componentStack.trim())))));
  }

  if (!mounted) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }

  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    onCatch: setErrorParams
  }, /*#__PURE__*/React.createElement(WorkshopContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(ScopeProvider, {
    axeResults: state.axeResults,
    schemas: state.schemas,
    registerProp: registerProp,
    scope: scope,
    setPropValue: setPropValue,
    story: story,
    title: title,
    unregisterProp: unregisterProp,
    value: state.value
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: null
  }, /*#__PURE__*/React.createElement(Card, {
    as: "main",
    height: "fill",
    ref: ref
  }, story && /*#__PURE__*/createElement(story.component))))));
});

function useScope() {
  const app = useContext(ScopeContext);

  if (!app) {
    throw new Error('Scope: missing context value');
  }

  return app;
}

function BooleanProp(props) {
  const {
    schema,
    value
  } = props;
  const {
    setPropValue
  } = useScope();
  return /*#__PURE__*/React.createElement(Flex, {
    as: "label",
    padding: 3
  }, /*#__PURE__*/React.createElement(Box, {
    marginRight: 2,
    style: {
      lineHeight: 0
    }
  }, /*#__PURE__*/React.createElement(Checkbox, {
    checked: value || false,
    onChange: event => setPropValue(schema.name, event.currentTarget.checked)
  })), /*#__PURE__*/React.createElement(Box, {
    paddingY: 1
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name)));
}

function NumberProp(props) {
  const {
    schema,
    value = ''
  } = props;
  const {
    setPropValue
  } = useScope();
  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name), /*#__PURE__*/React.createElement(Box, {
    marginTop: 2
  }, /*#__PURE__*/React.createElement(TextInput, {
    fontSize: 1,
    onChange: event => setPropValue(schema.name, Number(event.currentTarget.value)),
    padding: 2,
    value: value
  })));
}

function SelectProp(props) {
  const {
    schema,
    value: valueProp
  } = props;
  const {
    setPropValue
  } = useScope();
  const value = useMemo(() => {
    const entries = Object.entries(schema.options);

    for (const [k, v] of entries) {
      if (v === valueProp) {
        return k;
      }
    }

    return '';
  }, [schema, valueProp]);
  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name), /*#__PURE__*/React.createElement(Box, {
    marginTop: 2
  }, /*#__PURE__*/React.createElement(Select, {
    fontSize: 1,
    onChange: event => {
      const optionKey = event.currentTarget.value;
      const optionValue = schema.options[optionKey];
      setPropValue(schema.name, optionValue);
    },
    padding: 2,
    radius: 2,
    value: String(value || '')
  }, Object.entries(schema.options).map(([key]) => /*#__PURE__*/React.createElement("option", {
    key: key,
    value: key
  }, key)))));
}

function StringProp(props) {
  const {
    schema,
    value
  } = props;
  const {
    setPropValue
  } = useScope();
  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name), /*#__PURE__*/React.createElement(Box, {
    marginTop: 2
  }, /*#__PURE__*/React.createElement(TextInput, {
    fontSize: 1,
    onChange: event => setPropValue(schema.name, event.currentTarget.value),
    padding: 2,
    value: value || ''
  })));
}

function TextProp(props) {
  const {
    schema,
    value
  } = props;
  const {
    setPropValue
  } = useScope();
  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, schema.name), /*#__PURE__*/React.createElement(Box, {
    marginTop: 2
  }, /*#__PURE__*/React.createElement(TextArea, {
    onChange: event => setPropValue(schema.name, event.currentTarget.value),
    rows: 4,
    value: value || ''
  })));
}

function Prop(props) {
  const {
    schema,
    value
  } = props;

  if (schema.type === 'boolean') {
    return /*#__PURE__*/React.createElement(BooleanProp, {
      schema: schema,
      value: value
    });
  }

  if (schema.type === 'number') {
    return /*#__PURE__*/React.createElement(NumberProp, {
      schema: schema,
      value: value
    });
  }

  if (schema.type === 'select') {
    return /*#__PURE__*/React.createElement(SelectProp, {
      schema: schema,
      value: value
    });
  }

  if (schema.type === 'string') {
    return /*#__PURE__*/React.createElement(StringProp, {
      schema: schema,
      value: value
    });
  }

  if (schema.type === 'text') {
    return /*#__PURE__*/React.createElement(TextProp, {
      schema: schema,
      value: value
    });
  }

  return /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Text, {
    size: 1,
    weight: "semibold"
  }, "Unknown Prop type:", ' ', /*#__PURE__*/React.createElement("code", null, schema.name, ": ", schema.type)));
}

function useBoolean(name, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    schemas,
    unregisterProp,
    value
  } = useScope();
  useEffect(() => {
    registerProp({
      type: 'boolean',
      groupName,
      name,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, registerProp, unregisterProp]);
  const schema = schemas.find(s => s.name === name);
  return value[name] === undefined ? schema == null ? void 0 : schema.defaultValue : value[name];
}

function useNumber(name, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    schemas,
    unregisterProp,
    value
  } = useScope();
  useEffect(() => {
    registerProp({
      type: 'number',
      groupName,
      name,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, registerProp, unregisterProp]);
  const schema = schemas.find(s => s.name === name);
  return value[name] === undefined ? schema == null ? void 0 : schema.defaultValue : value[name];
}

function useSelect(name, options, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    schemas,
    unregisterProp,
    value
  } = useScope();
  useEffect(() => {
    registerProp({
      type: 'select',
      groupName,
      name,
      options: options,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, options, registerProp, unregisterProp]);
  const schema = schemas.find(s => s.name === name);
  return value[name] === undefined ? schema == null ? void 0 : schema.defaultValue : value[name];
}

function useString(name, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    schemas,
    unregisterProp,
    value
  } = useScope();
  useEffect(() => {
    registerProp({
      type: 'string',
      groupName,
      name,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, registerProp, unregisterProp]);
  const schema = schemas.find(s => s.name === name);
  return value[name] === undefined ? schema == null ? void 0 : schema.defaultValue : value[name];
}

function useText(name, defaultValue, groupName = 'Props') {
  const {
    registerProp,
    schemas,
    unregisterProp,
    value
  } = useScope();
  useEffect(() => {
    registerProp({
      type: 'text',
      groupName,
      name,
      defaultValue
    });
    return () => unregisterProp(name);
  }, [defaultValue, groupName, name, registerProp, unregisterProp]);
  const schema = schemas.find(s => s.name === name);
  return value[name] === undefined ? schema == null ? void 0 : schema.defaultValue : value[name];
}

const WORKSHOP_DEFAULT_FEATURES = {
  navbar: true
};

const btoa = typeof window === 'undefined' ? str => Buffer.from(str, 'binary').toString('base64') : window.btoa;
const atob = typeof window === 'undefined' ? str => Buffer.from(str, 'base64').toString('binary') : window.atob;
const uint8ArrayToBase64 = uint8array => {
  let str = '';

  for (let i = 0, {
    length
  } = uint8array; i < length; i++) {
    str += String.fromCharCode(uint8array[i]);
  }

  return btoa(str);
};

function base64ToUint8Array(base64) {
  const binStr = atob(base64);
  const len = binStr.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binStr.charCodeAt(i);
  }

  return bytes;
}

function decode(input) {
  if (input.length === 0) return '';
  const arr = base64ToUint8Array(input);
  return pako.inflate(arr, {
    to: 'string'
  });
}
function encode(input) {
  if (input.length === 0) return '';
  const arr = pako.deflate(input);
  return uint8ArrayToBase64(arr);
}

function useFrame() {
  const [frameElement, setFrameElement] = useState(null);
  const [ready, setReady] = useState(false);
  const msgQueueRef = useRef([]);
  useEffect(() => {
    if (!frameElement) return;

    const handleMessage = event => {
      const msg = event.data;

      if (isRecord(msg)) {
        if (msg.type === 'workshop/ready') {
          setReady(true);

          if (msgQueueRef.current.length) {
            for (const queuedMsg of msgQueueRef.current) {
              var _frameElement$content;

              (_frameElement$content = frameElement.contentWindow) == null ? void 0 : _frameElement$content.postMessage(queuedMsg, location.origin);
            }

            msgQueueRef.current = [];
          }

          window.removeEventListener('message', handleMessage);
          return;
        }

        if (msg.type === 'queue') {
          for (const _msg of msg.queue) {
            handleMessage({
              data: _msg
            });
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [frameElement]);
  const postMessage = useCallback(msg => {
    if (frameElement && ready) {
      var _frameElement$content2;

      (_frameElement$content2 = frameElement.contentWindow) == null ? void 0 : _frameElement$content2.postMessage(msg, location.origin);
    } else {
      msgQueueRef.current.push(msg);
    }
  }, [frameElement, ready]);
  const subscribe = useCallback(subscriber => {
    const handleMessage = event => {
      const msg = event.data;

      if (isRecord(msg) && typeof msg.type === 'string' && msg.type.startsWith('workshop/')) {
        subscriber(msg);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
  return {
    postMessage,
    ready,
    ref: setFrameElement,
    subscribe
  };
}

function useWorkshop() {
  const studio = useContext(WorkshopContext);

  if (!studio) {
    throw new Error('Workshop: missing context value');
  }

  return studio;
}

const VIEWPORT_OPTIONS = [{
  name: 'auto',
  title: 'Full',
  rect: {
    width: 'auto'
  }
}, {
  name: '768',
  title: '768px',
  rect: {
    width: 768
  }
}, {
  name: '375',
  title: '375px',
  rect: {
    width: 375,
    height: 667
  }
}, {
  name: '320',
  title: '320px',
  rect: {
    width: 320,
    height: 568
  }
}];
const ZOOM_OPTIONS = [{
  value: 0.5,
  title: '50%'
}, {
  value: 0.75,
  title: '75%'
}, {
  value: 1,
  title: '100%'
}, {
  value: 1.5,
  title: '150%'
}, {
  value: 2,
  title: '200%'
}, {
  value: 3,
  title: '300%'
}];

let _$1 = t => t,
    _t$1;
const Root = styled(Card)(_t$1 || (_t$1 = _$1`
  line-height: 0;
`));
function WorkshopNavbar(props) {
  const {
    scheme,
    setScheme,
    setViewport,
    setZoom,
    viewport,
    zoom
  } = props;
  const {
    pushLocation,
    scope,
    story,
    title
  } = useWorkshop();
  const handleToggleScheme = useCallback(event => {
    const target = event.currentTarget;
    setScheme(target.checked ? 'dark' : 'light');
  }, [setScheme]);
  const handleHomeClick = useCallback(event => {
    if (event.shiftKey || event.ctrlKey) return;
    event.preventDefault();
    pushLocation({
      path: '/'
    });
  }, [pushLocation]);
  return useMemo(() => {
    var _ZOOM_OPTIONS$find, _VIEWPORT_OPTIONS$fin;

    return /*#__PURE__*/React.createElement(Root, {
      borderBottom: true,
      paddingX: 3,
      paddingY: 2
    }, /*#__PURE__*/React.createElement(Flex, {
      align: "center"
    }, /*#__PURE__*/React.createElement(Box, {
      display: ['block', 'block', 'none']
    }, /*#__PURE__*/React.createElement(Button, {
      fontSize: 1,
      icon: MenuIcon,
      mode: "bleed",
      padding: 2
    })), /*#__PURE__*/React.createElement(Flex, {
      flex: 1,
      justify: ['center', 'center', 'flex-start'],
      padding: 2,
      sizing: "border",
      style: {
        minWidth: 250
      }
    }, /*#__PURE__*/React.createElement(Breadcrumbs, {
      separator: /*#__PURE__*/React.createElement(Text, {
        muted: true,
        size: 1
      }, "/"),
      space: 2
    }, /*#__PURE__*/React.createElement(Text, {
      size: 1,
      weight: "bold"
    }, /*#__PURE__*/React.createElement("a", {
      href: "/",
      onClick: handleHomeClick,
      style: {
        color: 'inherit'
      }
    }, title)), scope && /*#__PURE__*/React.createElement(Text, {
      align: "center",
      size: 1
    }, scope.title), story && /*#__PURE__*/React.createElement(Text, {
      size: 1
    }, story.title))), /*#__PURE__*/React.createElement(Box, {
      display: ['block', 'block', 'none']
    }, /*#__PURE__*/React.createElement(Button, {
      fontSize: 1,
      icon: ControlsIcon,
      mode: "bleed",
      padding: 2
    })), /*#__PURE__*/React.createElement(Box, {
      display: ['none', 'none', 'block'],
      flex: 1
    }, /*#__PURE__*/React.createElement(Flex, {
      align: "center",
      justify: "flex-end"
    }, /*#__PURE__*/React.createElement(LayerProvider, {
      zOffset: 100
    }, /*#__PURE__*/React.createElement(MenuButton, {
      button: /*#__PURE__*/React.createElement(Button, {
        fontSize: 1,
        iconRight: SelectIcon,
        mode: "ghost",
        padding: 2,
        style: {
          minWidth: 80
        },
        text: (_ZOOM_OPTIONS$find = ZOOM_OPTIONS.find(o => o.value === zoom)) == null ? void 0 : _ZOOM_OPTIONS$find.title
      }),
      id: "zoom-menu",
      menu: /*#__PURE__*/React.createElement(Menu, null, ZOOM_OPTIONS.map(option => /*#__PURE__*/React.createElement(MenuItem, {
        fontSize: 1,
        key: option.value,
        onClick: () => setZoom(option.value),
        padding: 2,
        selected: option.value === zoom,
        text: option.title
      }))),
      popover: {
        matchReferenceWidth: true
      },
      portal: true
    })), /*#__PURE__*/React.createElement(Flex, {
      marginLeft: 2
    }, /*#__PURE__*/React.createElement(LayerProvider, {
      zOffset: 100
    }, /*#__PURE__*/React.createElement(MenuButton, {
      button: /*#__PURE__*/React.createElement(Button, {
        fontSize: 1,
        iconRight: SelectIcon,
        mode: "ghost",
        padding: 2,
        style: {
          minWidth: 80
        },
        text: (_VIEWPORT_OPTIONS$fin = VIEWPORT_OPTIONS.find(o => o.name === viewport)) == null ? void 0 : _VIEWPORT_OPTIONS$fin.title
      }),
      id: "viewport-menu",
      menu: /*#__PURE__*/React.createElement(Menu, null, VIEWPORT_OPTIONS.map(option => /*#__PURE__*/React.createElement(MenuItem, {
        fontSize: 1,
        key: option.name,
        onClick: () => setViewport(option.name),
        padding: 2,
        selected: option.name === viewport,
        text: option.title
      }))),
      popover: {
        matchReferenceWidth: true
      },
      portal: true
    }))), /*#__PURE__*/React.createElement(Box, {
      marginLeft: 2
    }, /*#__PURE__*/React.createElement(Button, {
      as: "label",
      mode: "bleed",
      padding: 2
    }, /*#__PURE__*/React.createElement(Flex, {
      align: "center"
    }, /*#__PURE__*/React.createElement(Box, {
      marginRight: 2
    }, /*#__PURE__*/React.createElement(Text, {
      size: 1
    }, /*#__PURE__*/React.createElement(SunIcon, null))), /*#__PURE__*/React.createElement(Box, {
      style: {
        margin: '-4px 0'
      }
    }, /*#__PURE__*/React.createElement(Switch, {
      checked: scheme === 'dark',
      onChange: handleToggleScheme
    })), /*#__PURE__*/React.createElement(Box, {
      marginLeft: 2
    }, /*#__PURE__*/React.createElement(Text, {
      size: 1
    }, /*#__PURE__*/React.createElement(MoonIcon, null))))))))));
  }, [handleHomeClick, handleToggleScheme, scheme, scope, setViewport, setZoom, story, title, viewport, zoom]);
}

function WorkshopProvider(props) {
  const {
    axeResults,
    children,
    features = {},
    frameUrl,
    location,
    onLocationPush,
    onLocationReplace,
    registerProp,
    schemas,
    scopes,
    setPropValue,
    title,
    unregisterProp,
    value
  } = props;
  const workshop = useMemo(() => {
    const {
      scope,
      story
    } = resolveLocation(scopes, location.path);
    return {
      features,
      frameUrl,
      location,
      pushLocation: onLocationPush,
      replaceLocation: onLocationReplace,
      scope,
      scopes,
      story,
      title
    };
  }, [features, frameUrl, location, onLocationPush, onLocationReplace, scopes, title]);
  return /*#__PURE__*/React.createElement(WorkshopContext.Provider, {
    value: workshop
  }, /*#__PURE__*/React.createElement(ScopeProvider, {
    axeResults: axeResults,
    registerProp: registerProp,
    schemas: schemas,
    scope: workshop.scope,
    setPropValue: setPropValue,
    story: workshop.story,
    title: title,
    unregisterProp: unregisterProp,
    value: value
  }, children));
}

let _ = t => t,
    _t;
const Iframe = styled.iframe(_t || (_t = _`
  &:not([hidden]) {
    display: block;
  }
  border: 0;
  height: 100%;
  width: 100%;
`));
function WorkshopStoryCanvas(props) {
  const {
    frameRef,
    ready,
    scheme,
    viewport,
    zoom
  } = props;
  const {
    frameUrl,
    location,
    scope,
    story
  } = useWorkshop();
  const {
    value
  } = useScope();
  const [currentFrameUrl] = useState(() => `${frameUrl}?path=${location.path}&scheme=${scheme}&value=${encodeURIComponent(JSON.stringify(value))}`);
  const v = VIEWPORT_OPTIONS.find(o => o.name === viewport);
  const viewportW = v == null ? void 0 : v.rect.width;
  const viewportH = v == null ? void 0 : v.rect.height;
  const iframe = useMemo(() => /*#__PURE__*/React.createElement(Iframe, {
    hidden: !ready,
    ref: frameRef,
    src: currentFrameUrl,
    style: {
      transform: `scale(${zoom})`,
      transformOrigin: '0 0',
      width: `${100 / zoom}%`,
      height: `${100 / zoom}%`
    }
  }), [currentFrameUrl, frameRef, ready, zoom]);
  const hidden = !scope || !story;
  return useMemo(() => /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    flex: 3,
    overflow: "hidden"
  }, /*#__PURE__*/React.createElement(Card, {
    flex: 1,
    padding: 2,
    tone: "transparent"
  }, /*#__PURE__*/React.createElement(Flex, {
    align: "center",
    height: "fill",
    justify: "center"
  }, /*#__PURE__*/React.createElement(Container, {
    height: "fill",
    hidden: hidden,
    style: {
      maxWidth: viewportW === 'auto' ? undefined : `${(viewportW || 1) * zoom}px`,
      maxHeight: viewportH ? `${(viewportH || 1) * zoom}px` : undefined
    },
    width: "auto"
  }, /*#__PURE__*/React.createElement(Card, {
    height: "fill",
    overflow: "hidden",
    shadow: ready ? 1 : undefined,
    tone: ready ? undefined : 'inherit'
  }, !ready && /*#__PURE__*/React.createElement(Flex, {
    align: "center",
    height: "fill",
    justify: "center"
  }, /*#__PURE__*/React.createElement(Spinner, {
    muted: true
  })), iframe))))), [hidden, iframe, ready, viewportW, viewportH, zoom]);
}

function WorkshopStoryInspector() {
  var _axeResults$violation;

  // const {axeResults} =
  const {
    axeResults,
    schemas,
    value
  } = useScope();
  const hasA11yViolations = ((axeResults == null ? void 0 : (_axeResults$violation = axeResults.violations) == null ? void 0 : _axeResults$violation.length) || 0) > 0;
  const propsTab = useMemo(() => ({
    id: 'props',
    label: /*#__PURE__*/React.createElement(React.Fragment, null, "Props"),
    panel: /*#__PURE__*/React.createElement(React.Fragment, null, schemas.length === 0 && /*#__PURE__*/React.createElement(Box, {
      padding: 3
    }, /*#__PURE__*/React.createElement(Text, {
      muted: true,
      size: 1
    }, "No properties")), schemas.length > 0 && schemas.map((schema, schemaIndex) => /*#__PURE__*/React.createElement(Prop, {
      key: schemaIndex,
      schema: schema,
      value: value[schema.name] === undefined ? schema.defaultValue : value[schema.name]
    })))
  }), [schemas, value]);
  const axeTab = useMemo(() => features.axe , [axeResults, hasA11yViolations]);
  const tabs = useMemo(() => [propsTab, axeTab].filter(Boolean), [axeTab, propsTab]);
  const [tabId, setTabId] = useState(tabs.length > 0 ? tabs[0].id : null);
  const currentTab = tabs.find(tab => tab.id === tabId);
  const showTabs = tabs.length > 1;
  return useMemo(() => /*#__PURE__*/React.createElement(Card, {
    borderLeft: true,
    display: ['none', 'none', 'block'],
    flex: 1,
    overflow: "auto",
    style: {
      minWidth: 180,
      maxWidth: 300
    }
  }, showTabs && /*#__PURE__*/React.createElement(Layer, {
    style: {
      position: 'sticky',
      top: 0
    }
  }, /*#__PURE__*/React.createElement(Card, {
    padding: 2,
    shadow: 1
  }, /*#__PURE__*/React.createElement(TabList, {
    space: 1
  }, tabs.map(tab => /*#__PURE__*/React.createElement(Tab, {
    "aria-controls": `${tab.id}-panel`,
    fontSize: 1,
    id: tab.id,
    key: tab.id,
    label: tab.label,
    onClick: () => setTabId(tab.id),
    selected: tab.id === tabId,
    tone: tab.tone
  }))))), showTabs && tabs.map(tab => /*#__PURE__*/React.createElement(TabPanel, {
    "aria-labelledby": `${tab.id}-tab`,
    hidden: tab.id === tabId,
    id: `${tab.id}-panel`,
    key: tab.id,
    overflow: "auto",
    padding: 2
  }, tab.panel)), !showTabs && currentTab && /*#__PURE__*/React.createElement(Box, {
    padding: 2
  }, currentTab.panel)), [currentTab, showTabs, tabId, tabs]);
}

function buildMenuItems(collections, node, name) {
  if (Array.isArray(node.stories)) {
    return {
      type: 'scope',
      name: node.name,
      title: node.title,
      scope: node
    };
  }

  const coll = collections.find(c => c.name === name);
  return {
    type: 'list',
    name,
    title: coll ? coll.title : name,
    items: Object.entries(node).map(([name, child]) => buildMenuItems((coll == null ? void 0 : coll.children) || [], child, name))
  };
}
function buildMenu(collections, scopes) {
  const scopeMap = {}; // Merge scopes

  for (const scope of scopes) {
    if (scopeMap[scope.name]) {
      scopeMap[scope.name] = _extends({}, scopeMap[scope.name], {
        title: scope.title,
        stories: scopeMap[scope.name].stories.concat(scope.stories)
      });
    } else {
      scopeMap[scope.name] = scope;
    }
  }

  let tree = {};

  for (const [name, scope] of Object.entries(scopeMap)) {
    tree = set(tree, name, scope);
  }

  return buildMenuItems([{
    children: collections
  }], tree);
}

function WorkshopStoryNav(props) {
  const {
    collections = []
  } = props;
  const {
    scopes
  } = useWorkshop();
  const menu = useMemo(() => buildMenu(collections, scopes), [collections, scopes]);
  return useMemo(() => /*#__PURE__*/React.createElement(Card, {
    borderRight: true,
    display: ['none', 'none', 'block'],
    flex: 1,
    overflow: "auto",
    style: {
      minWidth: 180,
      maxWidth: 300
    }
  }, menu.type === 'list' && /*#__PURE__*/React.createElement(Box, {
    padding: 3
  }, /*#__PURE__*/React.createElement(Tree, {
    space: 1
  }, /*#__PURE__*/React.createElement(MenuItems, {
    items: menu.items
  })))), [menu]);
}

function MenuItems(props) {
  const {
    basePath = '',
    items
  } = props;
  const {
    location,
    scope: currentScope,
    story: currentStory,
    pushLocation
  } = useWorkshop();
  const handleStoryClick = useCallback(event => {
    event.preventDefault();
    const target = event.currentTarget;
    const targetPath = target.getAttribute('data-path');

    if (targetPath) {
      pushLocation({
        path: targetPath
      });
    }
  }, [pushLocation]);
  return useMemo(() => {
    if (items.length === 0) {
      return null;
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, items.map((item, itemIndex) => {
      if (item.type === 'list') {
        const path = `${basePath}/${item.name}`;
        return /*#__PURE__*/React.createElement(TreeItem, {
          expanded: location.path.startsWith(path + '/'),
          fontSize: 1,
          key: item.name || itemIndex,
          padding: 2,
          text: item.title,
          weight: "semibold"
        }, /*#__PURE__*/React.createElement(MenuItems, {
          basePath: path,
          items: item.items
        }));
      }

      if (item.type === 'scope') {
        return /*#__PURE__*/React.createElement(TreeItem, {
          expanded: item.scope === currentScope,
          fontSize: 1,
          key: item.name,
          padding: 2,
          text: item.title,
          weight: "semibold"
        }, item.scope.stories.map(story => /*#__PURE__*/React.createElement(TreeItem, {
          "data-path": `/${item.scope.name}/${story.name}`,
          fontSize: 1,
          href: `/${item.scope.name}/${story.name}`,
          key: story.name,
          onClick: handleStoryClick,
          padding: 2,
          selected: currentStory === story,
          text: story.title
        })));
      }

      return /*#__PURE__*/React.createElement(TreeItem, {
        key: itemIndex,
        text: "unknown"
      });
    }));
  }, [basePath, currentScope, currentStory, handleStoryClick, items, location.path]);
}

function _sortScopes(a, b) {
  if (a.name < b.name) return -1;
  if (a.name > b.name) return 1;
  return 0;
}

function encodeValue(val) {
  return encode(JSON.stringify(val));
}

function decodeValue(val) {
  return JSON.parse(decode(val));
}

function Workshop(props) {
  var _location$query;

  const {
    collections,
    features: featuresProp = {},
    frameUrl,
    location,
    onLocationPush,
    onLocationReplace,
    scheme,
    scopes: scopesProp,
    setScheme,
    title
  } = props;
  const features = useMemo(() => _extends({}, WORKSHOP_DEFAULT_FEATURES, featuresProp), [featuresProp]);
  const {
    postMessage,
    ready,
    ref: frameRef,
    subscribe
  } = useFrame();
  const rawValue = (_location$query = location.query) == null ? void 0 : _location$query.value;
  const [encodedValue, setEncodedValue] = useState(typeof rawValue === 'string' ? rawValue : null);
  const encodedValueRef = useRef(encodedValue);
  const [state, dispatch] = useReducer(workshopReducer, {
    axeResults: null,
    schemas: [],
    value: encodedValue ? decodeValue(encodedValue) : {}
  });
  const [viewport, setViewport] = useState('auto');
  const [zoom, setZoom] = useState(1);
  const [boundaryElement, setBoundaryElement] = useState(null);
  const [portalElement, setPortalElement] = useState(null);
  const scopes = useMemo(() => scopesProp.sort(_sortScopes), [scopesProp]);
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  useEffect(() => {
    var _loc$query, _loc$query2;

    const loc = locationRef.current;

    if (encodedValue && ((_loc$query = loc.query) == null ? void 0 : _loc$query.value) !== encodedValue) {
      const newLoc = _extends({}, loc, {
        query: _extends({}, loc.query, {
          value: encodedValue
        })
      });

      onLocationReplace(newLoc);
    } else if ((_loc$query2 = loc.query) != null && _loc$query2.value && !encodedValue) {
      const query = _extends({}, loc.query);

      delete query.value;

      const newLoc = _extends({}, loc, {
        query
      });

      onLocationReplace(newLoc);
    }
  }, [encodedValue, onLocationReplace]);
  const registerProp = useCallback(schema => {
    const msg = {
      type: 'workshop/registerProp',
      schema
    };
    dispatch(msg);
  }, []);
  const unregisterProp = useCallback(name => {
    const msg = {
      type: 'workshop/unregisterProp',
      name
    };
    dispatch(msg);
  }, []);
  const setPropValue = useCallback((name, value) => {
    const msg = {
      type: 'workshop/setPropValue',
      name,
      value
    };
    dispatch(msg);
    postMessage(msg);
  }, [postMessage]);
  useEffect(() => {
    const msg = {
      type: 'workshop/main/setLocation',
      path: location.path,
      scheme
    };
    postMessage(msg);
  }, [location.path, postMessage, scheme]);
  useEffect(() => {
    function handleMsg(msg) {
      if (!isRecord(msg)) return;
      if (typeof msg.type !== 'string') return;
      if (!msg.type.startsWith('workshop/')) return;
      dispatch(msg);
    }

    return subscribe(handleMsg);
  }, [subscribe]);
  useEffect(() => {
    if (Object.keys(state.value).length) {
      const enc = encodeValue(state.value);
      setEncodedValue(enc);
      encodedValueRef.current = enc;
    } else {
      setEncodedValue(null);
      encodedValueRef.current = null;
    }
  }, [state.value]);
  useEffect(() => {
    setEncodedValue(typeof rawValue === 'string' ? rawValue : null);
  }, [rawValue]);
  useEffect(() => {
    if (encodedValue !== encodedValueRef.current) {
      const msg = {
        type: 'workshop/setValue',
        value: encodedValue ? decodeValue(encodedValue) : {}
      };
      dispatch(msg);
      postMessage(msg);
    }
  }, [encodedValue, postMessage]);
  const children = useMemo(() => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    height: "fill",
    ref: setBoundaryElement
  }, features.navbar && /*#__PURE__*/React.createElement(WorkshopNavbar, {
    scheme: scheme,
    setScheme: setScheme,
    setViewport: setViewport,
    setZoom: setZoom,
    viewport: viewport,
    zoom: zoom
  }), /*#__PURE__*/React.createElement(Flex, {
    flex: 1
  }, /*#__PURE__*/React.createElement(WorkshopStoryNav, {
    collections: collections
  }), /*#__PURE__*/React.createElement(WorkshopStoryCanvas, {
    frameRef: frameRef,
    ready: ready,
    scheme: scheme,
    viewport: viewport,
    zoom: zoom
  }), /*#__PURE__*/React.createElement(WorkshopStoryInspector, null))), /*#__PURE__*/React.createElement("div", {
    "data-portal": "",
    ref: setPortalElement
  })), [collections, features, frameRef, ready, scheme, setScheme, // state.axeResults,
  // state.value,
  viewport, zoom]);
  return /*#__PURE__*/React.createElement(ToastProvider, null, /*#__PURE__*/React.createElement(BoundaryElementProvider, {
    element: boundaryElement
  }, /*#__PURE__*/React.createElement(PortalProvider, {
    element: portalElement
  }, /*#__PURE__*/React.createElement(WorkshopProvider, {
    axeResults: state.axeResults,
    features: features,
    frameUrl: frameUrl,
    location: location,
    onLocationPush: onLocationPush,
    onLocationReplace: onLocationReplace,
    schemas: state.schemas,
    scopes: scopes,
    registerProp: registerProp,
    setPropValue: setPropValue,
    title: title,
    unregisterProp: unregisterProp,
    value: state.value
  }, children))));
}

function useAction(name) {
  const {
    scope,
    story
  } = useScope();
  return useCallback((...args) => {
    if (!scope || !story) return; // eslint-disable-next-line no-console

    console.log(`[${scope.name}/${story.name}]`, name, ...args);
  }, [scope, story, name]);
}

export { Prop, ScopeProvider, Workshop, WorkshopFrame, defineScope, useAction, useBoolean, useNumber, useScope, useSelect, useString, useText };
//# sourceMappingURL=sanity-ui-workshop.modern.js.map
